import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface FaqRow {
  readonly faqId: string;
  readonly faqCode: string;
  readonly question: string;
  readonly category: string;
  readonly status: string;
  readonly helpfulVotes: number;
  readonly totalVotes: number;
  readonly helpfulnessRating: string;
}

const STATUSES = ['', 'DRAFT', 'IN_REVIEW', 'APPROVED', 'PUBLISHED', 'REJECTED', 'ARCHIVED', 'RETIRED', 'OBSOLETE'];
const CATEGORIES = ['', 'PROCESS', 'PRODUCT', 'POLICY', 'TECHNICAL', 'COMPLIANCE', 'SALES', 'FINANCE', 'HR', 'OPERATIONS', 'CUSTOMER', 'TRAINING', 'GENERAL'];

/**
 * M35 — FAQ Management Dashboard (§33). Register of frequently-asked questions
 * (T466) across the 12 knowledge categories, carrying the §8 lifecycle and the
 * §17 helpfulness signal (helpful votes / total votes / helpfulness rating).
 * Users record a helpful / not-helpful vote per FAQ which updates the tally.
 * Filterable by category, status and free-text search. Tenant isolation is
 * enforced server-side (LAW 4).
 */
export function FaqManagementDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<FaqRow[]>([]);
  const [total, setTotal] = useState(0);
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [newQuestion, setNewQuestion] = useState('');
  const [newAnswer, setNewAnswer] = useState('');
  const [newCategory, setNewCategory] = useState('GENERAL');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (status) params.set('status', status);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: FaqRow[]; total: number }>(`/v1/kms/faqs${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, category, status, q]);

  useEffect(() => load(), [load]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newQuestion.trim()) { setActionError(t('m35.faq.questionRequired')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/faqs`, {
        method: 'POST',
        body: { question: newQuestion.trim(), answer: newAnswer.trim() || undefined, category: newCategory },
        token,
      });
      setNotice(t('m35.common.created', { name: newQuestion.trim() }));
      setNewQuestion('');
      setNewAnswer('');
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.faq.notAuthorisedManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const publish = async (r: FaqRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusyId(r.faqId);
    try {
      await apiRequest(`/v1/kms/faqs/${r.faqId}/publish`, { method: 'POST', token });
      setNotice(t('m35.common.notice', { code: r.faqCode, label: t('m35.common.act.published') }));
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.common.notAuthorisedAction', { perm: 'M35_FAQ_MANAGE' }));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (r: FaqRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusyId(r.faqId);
    try {
      await apiRequest(`/v1/kms/faqs/${r.faqId}`, { method: 'DELETE', token });
      setNotice(t('m35.common.notice', { code: r.faqCode, label: t('m35.common.act.deleted') }));
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.faq.notAuthorisedManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusyId(null);
    }
  };

  const vote = async (faqId: string, helpful: boolean): Promise<void> => {
    setActionError(null);
    setBusyId(faqId);
    try {
      await apiRequest<{ data: FaqRow }>(`/v1/kms/faqs/${faqId}/vote`, { method: 'POST', body: { helpful }, token });
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.faq.notAuthorisedVote'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main aria-label="faq-dashboard">
      <h1>{t('m35.faq.title')}</h1>

      <form aria-label="faq-form" onSubmit={(e) => void create(e)}>
        <label>
          {t('m35.faq.question')}
          <input aria-label="field-faq-question" value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)} />
        </label>
        <label>
          {t('m35.faq.answer')}
          <input aria-label="field-faq-answer" value={newAnswer} onChange={(e) => setNewAnswer(e.target.value)} />
        </label>
        <label>
          {t('m35.common.category')}
          <select aria-label="field-faq-category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
            {CATEGORIES.filter((c) => c).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <Button variant="primary" type="submit" aria-label="submit-faq" disabled={busy}>{busy ? t('m35.common.saving') : t('m35.faq.createBtn')}</Button>
      </form>
      {notice && <p aria-label="faq-action-notice">{notice}</p>}

      <div className="filters">
        <label>
          {t('m35.common.category')}
          <select aria-label="filter-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.common.search')}
          <input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m35.faq.searchPh')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {actionError && <p role="alert" aria-label="faq-action-error">{actionError}</p>}
      {loading ? <p>{t('m35.common.loading')}</p> : (
        <>
          <p aria-label="faq-count">{t('m35.faq.count', { n: total })}</p>
          <DataTable<FaqRow>
            ariaLabel="faq-table"
            testId="faq-table"
            rowKey={(r) => r.faqId}
            rowDataAttrs={(r) => ({ 'data-status': r.status, 'data-category': r.category })}
            rows={rows}
            columns={[
              { key: 'code', header: t('m35.common.code'), render: (r) => r.faqCode },
              { key: 'question', header: t('m35.faq.question'), render: (r) => r.question },
              { key: 'category', header: t('m35.common.category'), render: (r) => r.category },
              { key: 'status', header: t('m35.common.status'), render: (r) => r.status },
              { key: 'helpful', header: t('m35.faq.colHelpful'), render: (r) => r.helpfulVotes, cellAriaLabel: (r) => `faq-helpful-${r.faqCode}` },
              { key: 'total', header: t('m35.faq.colTotal'), render: (r) => r.totalVotes, cellAriaLabel: (r) => `faq-total-${r.faqCode}` },
              { key: 'rating', header: t('m35.faq.colRating'), render: (r) => r.helpfulnessRating, cellAriaLabel: (r) => `faq-rating-${r.faqCode}` },
              { key: 'vote', header: t('m35.faq.colVote'), render: (r) => (
                <>
                  <Button variant="secondary" type="button" aria-label={`vote-helpful-${r.faqCode}`} disabled={busyId === r.faqId} onClick={() => void vote(r.faqId, true)}>{t('m35.faq.btnHelpful')}</Button>
                  <Button variant="secondary" type="button" aria-label={`vote-unhelpful-${r.faqCode}`} disabled={busyId === r.faqId} onClick={() => void vote(r.faqId, false)}>{t('m35.faq.btnUnhelpful')}</Button>
                </>
              ) },
              { key: 'actions', header: t('m35.common.actions'), render: (r) => (
                <>
                  {r.status !== 'PUBLISHED' && (
                    <Button variant="primary" type="button" aria-label={`publish-${r.faqCode}`} disabled={busyId === r.faqId} onClick={() => void publish(r)}>{t('m35.common.btnPublish')}</Button>
                  )}
                  <Button variant="danger" type="button" aria-label={`delete-${r.faqCode}`} disabled={busyId === r.faqId} onClick={() => void remove(r)}>{t('m35.common.delete')}</Button>
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
